import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import {
  Car, Calendar, ClipboardList, ShieldCheck, ShieldX, X, Check,
  Clock, CircleAlert as AlertCircle, Fuel, Eye, Ban, MessageCircle,
  ChevronDown, ChevronUp, Phone, UserCheck, Plus, Trash2, Settings
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useVeiculosFrota, getVeiculoLabel } from '../hooks/useVeiculosFrota';

const getVehicleColor = (v: string) => {
  const n = (v || '').toLowerCase();
  if (n.includes('fiat strada')) return '#3B82F6';
  if (n.includes('fiat toro')) return '#10B981';
  if (n.includes('renault kwid') || n.includes('kwid')) return '#F59E0B';
  if (n.includes('renault oroch') || n.includes('oroch')) return '#8B5CF6';
  if (n.includes('saveiro')) return '#EC4899';
  if (n.includes('jeep compass') || n.includes('compass')) return '#EF4444';
  return '#6366F1';
};

const statusLabels: Record<string, { label: string; color: string; bg: string; border: string }> = {
  pending: { label: 'Aguardando', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  aguardando_checklist: { label: 'Checklist Pendente', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  approved: { label: 'Aprovado', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  rejected: { label: 'Reprovado', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
};

const fuelBars: Record<string, number> = { vazio: 0, '1/4': 1, '1/2': 2, '3/4': 3, cheio: 4 };

interface Reserva {
  id: string;
  nome: string;
  motivo: string;
  empresa: string;
  veiculo: string;
  data_retirada: string;
  horario_retirada: string;
  data_entrega: string;
  horario_entrega: string;
  status: string;
  observacoes_frota?: string;
  aprovado_em?: string;
  usuario_id?: string;
  telefone?: string;
  checklist_token?: string;
}

interface Checklist {
  id: string;
  reserva_id: string;
  km_saida: number;
  nivel_combustivel: string;
  pneus_ok: boolean;
  documentacao_ok: boolean;
  avarias?: string;
  observacoes?: string;
  nome_condutor?: string;
  created_at: string;
  reserva_veiculos?: Reserva;
}

interface BannedUser {
  id: string;
  nome: string;
  email: string;
  setor: string;
}

interface ApprovalState {
  reserva: Reserva | null;
  action: 'approve' | 'reject' | null;
  veiculoSelecionado: string;
  obs: string;
}

function buildWhatsAppUrl(phone: string, message: string): string {
  const digits = phone.replace(/\D/g, '');
  const number = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

export const FrotaPortal: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'pendentes' | 'reservas' | 'checklists' | 'bloqueados' | 'veiculos'>('pendentes');
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvalState, setApprovalState] = useState<ApprovalState>({ reserva: null, action: null, veiculoSelecionado: '', obs: '' });
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedChecklist, setExpandedChecklist] = useState<string | null>(null);
  const [whatsappModal, setWhatsappModal] = useState<{ url: string; nome: string } | null>(null);
  const [showAddVeiculoModal, setShowAddVeiculoModal] = useState(false);
  const [newVeiculo, setNewVeiculo] = useState({ modelo: '', placa: '', cor_tag: '#3B82F6', observacao: '' });
  const [addingVeiculo, setAddingVeiculo] = useState(false);

  const { veiculos: veiculosFrota, loading: veiculosLoading, addVeiculo, removeVeiculo, loadVeiculos } = useVeiculosFrota();

  const isAdmin = user?.role === 'admin' || user?.setor === 'RH';
  const isFrota = user?.role === 'frota';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, cRes, bRes] = await Promise.all([
        supabase.from('reserva_veiculos').select('*').order('created_at', { ascending: false }),
        supabase.from('veiculo_checklist').select('*, reserva_veiculos(*)').order('created_at', { ascending: false }),
        supabase.from('usuarios').select('id, nome, email, setor').eq('banido_veiculos', true),
      ]);
      if (rRes.data) setReservas(rRes.data);
      if (cRes.data) setChecklists(cRes.data as Checklist[]);
      if (bRes.data) setBannedUsers(bRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApproval = async () => {
    if (!approvalState.reserva || !approvalState.action) return;
    if (approvalState.action === 'approve' && !approvalState.veiculoSelecionado) {
      toast.error('Selecione o veículo a liberar.');
      return;
    }
    if (approvalState.action === 'reject' && !approvalState.obs.trim()) {
      toast.error('Informe o motivo da reprovação.');
      return;
    }

    setApprovalLoading(true);
    try {
      const token = crypto.randomUUID();
      const newStatus = approvalState.action === 'approve' ? 'aguardando_checklist' : 'rejected';
      const payload: Record<string, any> = {
        status: newStatus,
        observacoes_frota: approvalState.obs || null,
        aprovado_por_id: user?.dbId || null,
        aprovado_em: new Date().toISOString(),
      };
      if (approvalState.action === 'approve') {
        payload.veiculo = approvalState.veiculoSelecionado;
        payload.checklist_token = token;
      }

      const { error } = await supabase.from('reserva_veiculos').update(payload).eq('id', approvalState.reserva.id);
      if (error) throw error;

      const r = approvalState.reserva;
      const action = approvalState.action;
      const veiculo = approvalState.veiculoSelecionado;
      const obs = approvalState.obs;

      setApprovalState({ reserva: null, action: null, veiculoSelecionado: '', obs: '' });
      await loadData();

      if (r.telefone) {
        const link = `${import.meta.env.VITE_APP_URL || window.location.origin}/checklist/${token}`;
        let message = '';
        if (action === 'approve') {
          message = `Olá ${r.nome}! ✅ Sua reserva de veículo foi *APROVADA*.\n\n🚗 Veículo: *${veiculo}*\n📅 Retirada: ${r.data_retirada} às ${r.horario_retirada?.slice(0, 5)}\n📅 Devolução: ${r.data_entrega} às ${r.horario_entrega?.slice(0, 5)}\n\n📋 Preencha o voucher de saída antes de retirar o veículo:\n${link}`;
          if (obs) message += `\n\n📝 Observação: ${obs}`;
        } else {
          message = `Olá ${r.nome}! ❌ Sua solicitação de veículo para ${r.data_retirada} foi *REPROVADA*.`;
          if (obs) message += `\n\n📝 Motivo: ${obs}`;
        }
        setWhatsappModal({ url: buildWhatsAppUrl(r.telefone, message), nome: r.nome });
      } else {
        toast.success(action === 'approve' ? `Aprovado — ${veiculo}` : 'Reprovado.');
      }
    } catch (err: any) {
      toast.error('Erro: ' + (err.message || 'Tente novamente'));
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleUnban = async (usuarioId: string, nome?: string) => {
    const { error } = await supabase.from('usuarios').update({ banido_veiculos: false }).eq('id', usuarioId);
    if (error) {
      toast.error('Erro ao desbloquear usuário.');
      return;
    }
    toast.success(nome ? `${nome} desbloqueado(a) com sucesso.` : 'Bloqueio removido.');
    setBannedUsers((prev) => prev.filter((u) => u.id !== usuarioId));
    loadData();
  };

  const pending = reservas.filter((r) => r.status === 'pending');
  const filtered = statusFilter === 'all' ? reservas : reservas.filter((r) => r.status === statusFilter);

  const stats = {
    pending: reservas.filter((r) => r.status === 'pending').length,
    awaitingChecklist: reservas.filter((r) => r.status === 'aguardando_checklist').length,
    active: reservas.filter((r) => r.status === 'approved').length,
    checklists: checklists.length,
    banned: bannedUsers.length,
  };

  if (!isAdmin && !isFrota) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Acesso restrito ao analista de frota.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="font-display text-headline-lg text-on-surface">Portal de Frota</h1>
          <p className="text-secondary text-sm mt-1">Gerenciamento de veículos e checklists</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Solicitações Pendentes', value: stats.pending, color: 'amber', icon: Clock },
            { label: 'Aguardando Checklist', value: stats.awaitingChecklist, color: 'blue', icon: ClipboardList },
            { label: 'Reservas Ativas', value: stats.active, color: 'green', icon: Car },
            { label: 'Checklists Recebidos', value: stats.checklists, color: 'gray', icon: Eye },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant dark:border-gray-700 p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 bg-${color}-100`}>
                <Icon className={`w-5 h-5 text-${color}-600`} />
              </div>
              <div className="text-2xl font-bold text-on-surface">{value}</div>
              <div className="text-xs text-secondary mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant dark:border-gray-700 overflow-hidden">
          <div className="flex border-b border-outline-variant">
            {([
              { key: 'pendentes', label: 'Pendentes', count: stats.pending },
              { key: 'reservas', label: 'Todas as Reservas' },
              { key: 'checklists', label: 'Checklists', count: stats.checklists },
              { key: 'bloqueados', label: 'Bloqueados', count: stats.banned },
              { key: 'veiculos', label: 'Veiculos', count: veiculosFrota.length },
            ] as const).map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 px-4 py-3.5 text-sm font-medium transition-colors relative ${
                  activeTab === key
                    ? 'text-primary bg-primary/10'
                    : 'text-secondary hover:text-on-surface hover:bg-surface-container-low'
                }`}
              >
                {label}
                {count !== undefined && count > 0 && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === key ? 'bg-primary text-white' : 'bg-outline-variant text-secondary'
                  }`}>
                    {count}
                  </span>
                )}
                {activeTab === key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'pendentes' && (
                <div>
                  {pending.length === 0 ? (
                    <div className="text-center py-16">
                      <Check className="w-12 h-12 text-green-300 mx-auto mb-3" />
                      <p className="text-secondary">Nenhuma solicitação pendente</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {pending.map((r) => (
                        <div key={r.id} className="p-5 hover:bg-surface-container-low transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-on-surface">{r.nome}</span>
                                <span className="text-secondary">—</span>
                                <span className="text-sm text-secondary">{r.empresa}</span>
                              </div>
                              <p className="text-sm text-secondary mb-2">{r.motivo}</p>
                              <div className="flex flex-wrap gap-3 text-xs text-secondary">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  Retirada: {r.data_retirada} às {r.horario_retirada?.slice(0, 5)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  Devolução: {r.data_entrega} às {r.horario_entrega?.slice(0, 5)}
                                </span>
                                {r.telefone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3.5 h-3.5 text-green-600" />
                                    {r.telefone}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => setApprovalState({ reserva: r, action: 'approve', veiculoSelecionado: '', obs: '' })}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                              >
                                <ShieldCheck className="w-4 h-4" />
                                Aprovar
                              </button>
                              <button
                                onClick={() => setApprovalState({ reserva: r, action: 'reject', veiculoSelecionado: '', obs: '' })}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-error/10 border border-error text-error rounded-lg hover:bg-error/20 transition-colors text-sm font-medium"
                              >
                                <ShieldX className="w-4 h-4" />
                                Reprovar
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reservas' && (
                <div>
                  <div className="p-4 border-b border-outline-variant flex flex-wrap gap-2">
                    {(['all', 'pending', 'aguardando_checklist', 'approved', 'rejected'] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          statusFilter === s ? 'bg-primary text-white' : 'bg-outline-variant/20 text-secondary hover:bg-outline-variant/30'
                        }`}
                      >
                        {s === 'all' ? 'Todos' : statusLabels[s]?.label ?? s}
                        {s !== 'all' && (
                          <span className="ml-1.5 opacity-70">({reservas.filter((r) => r.status === s).length})</span>
                        )}
                      </button>
                    ))}
                  </div>
                  {filtered.length === 0 ? (
                    <div className="text-center py-16">
                      <Car className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                      <p className="text-secondary">Nenhuma reserva</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filtered.map((r) => {
                        const cfg = statusLabels[r.status] || statusLabels.pending;
                        return (
                          <div key={r.id} className="p-4 hover:bg-surface-container-low transition-colors">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div
                                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: getVehicleColor(r.veiculo) + '20' }}
                                >
                                  <Car className="w-4 h-4" style={{ color: getVehicleColor(r.veiculo) }} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-on-surface text-sm">{r.veiculo}</span>
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
                                      {cfg.label}
                                    </span>
                                  </div>
                                  <div className="text-sm text-secondary">{r.nome} — {r.empresa}</div>
                                  <div className="text-xs text-secondary">{r.motivo}</div>
                                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-secondary">
                                    <span>{r.data_retirada} às {r.horario_retirada?.slice(0, 5)}</span>
                                    <span>→</span>
                                    <span>{r.data_entrega} às {r.horario_entrega?.slice(0, 5)}</span>
                                    {r.telefone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />{r.telefone}
                                      </span>
                                    )}
                                  </div>
                                  {r.observacoes_frota && (
                                    <p className="text-xs text-secondary mt-0.5 italic">Obs: {r.observacoes_frota}</p>
                                  )}
                                </div>
                              </div>
                              {r.status === 'pending' && (
                                <button
                                  onClick={() => setApprovalState({ reserva: r, action: 'approve', veiculoSelecionado: '', obs: '' })}
                                  className="flex-shrink-0 text-xs px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium"
                                >
                                  Aprovar
                                </button>
                              )}
                              {r.usuario_id && r.status !== 'pending' && (
                                <button
                                  onClick={() => handleUnban(r.usuario_id!)}
                                  title="Desbloquear usuário"
                                  className="flex-shrink-0 text-xs px-2 py-1.5 bg-surface-container-low border border-outline-variant text-secondary rounded-lg hover:bg-surface-container transition-colors"
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'checklists' && (
                <div>
                  {checklists.length === 0 ? (
                    <div className="text-center py-16">
                      <ClipboardList className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                      <p className="text-secondary">Nenhum checklist recebido</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {checklists.map((c) => {
                        const rv = c.reserva_veiculos;
                        const isExpanded = expandedChecklist === c.id;
                        const bars = fuelBars[c.nivel_combustivel] ?? 2;
                        return (
                          <div key={c.id} className="p-4">
                            <button
                              onClick={() => setExpandedChecklist(isExpanded ? null : c.id)}
                              className="w-full text-left"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  <div
                                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: getVehicleColor(rv?.veiculo || '') + '20' }}
                                  >
                                    <Car className="w-4 h-4" style={{ color: getVehicleColor(rv?.veiculo || '') }} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-semibold text-on-surface text-sm">{rv?.veiculo || '—'}</div>
                                    <div className="text-sm text-secondary">{c.nome_condutor || rv?.nome || '—'}</div>
                                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-secondary">
                                      <span>KM saída: <span className="font-medium text-on-surface">{c.km_saida?.toLocaleString('pt-BR')}</span></span>
                                      <span className="flex items-center gap-1">
                                        <Fuel className="w-3 h-3 text-amber-500" />
                                        {c.nivel_combustivel}
                                      </span>
                                      <span className="flex gap-0.5">
                                        {[0, 1, 2, 3].map((i) => (
                                          <div key={i} className={`w-1.5 h-2.5 rounded-sm ${i < bars ? 'bg-amber-400' : 'bg-gray-200'}`} />
                                        ))}
                                      </span>
                                      <span className="text-secondary">
                                        {new Date(c.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <div className="flex gap-1">
                                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${c.pneus_ok ? 'bg-green-100' : 'bg-error/10'}`}>
                                      <div className={`w-2 h-2 rounded-full ${c.pneus_ok ? 'bg-green-500' : 'bg-error'}`} />
                                    </span>
                                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${c.documentacao_ok ? 'bg-green-100' : 'bg-error/10'}`}>
                                      <div className={`w-2 h-2 rounded-full ${c.documentacao_ok ? 'bg-green-500' : 'bg-error'}`} />
                                    </span>
                                  </div>
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-secondary" /> : <ChevronDown className="w-4 h-4 text-secondary" />}
                                </div>
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="mt-4 ml-12 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div className="bg-surface-container-low rounded-lg p-3">
                                  <div className="text-xs text-secondary mb-1">Condições</div>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-medium ${c.pneus_ok ? 'text-green-700' : 'text-error'}`}>
                                      Pneus: {c.pneus_ok ? 'OK' : 'Irregular'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs font-medium ${c.documentacao_ok ? 'text-green-700' : 'text-error'}`}>
                                      Documentação: {c.documentacao_ok ? 'Presente' : 'Ausente'}
                                    </span>
                                  </div>
                                </div>
                                <div className="bg-surface-container-low rounded-lg p-3">
                                  <div className="text-xs text-secondary mb-1">Reserva</div>
                                  <div className="text-xs text-on-surface">{rv?.motivo}</div>
                                  <div className="text-xs text-secondary mt-0.5">{rv?.empresa}</div>
                                  <div className="text-xs text-secondary">{rv?.data_retirada} → {rv?.data_entrega}</div>
                                </div>
                                {c.avarias && (
                                  <div className="sm:col-span-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
                                    <div className="text-xs text-orange-600 font-semibold mb-1">Avarias registradas</div>
                                    <p className="text-xs text-orange-700">{c.avarias}</p>
                                  </div>
                                )}
                                {c.observacoes && (
                                  <div className="sm:col-span-2 bg-surface-container-low rounded-lg p-3">
                                    <div className="text-xs text-secondary mb-1">Observações</div>
                                    <p className="text-xs text-secondary">{c.observacoes}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'bloqueados' && (
                <div>
                  {bannedUsers.length === 0 ? (
                    <div className="text-center py-16">
                      <UserCheck className="w-12 h-12 text-green-300 mx-auto mb-3" />
                      <p className="text-on-surface font-medium">Nenhum usuário bloqueado</p>
                      <p className="text-secondary text-sm mt-1">Todos os colaboradores podem fazer reservas normalmente.</p>
                    </div>
                  ) : (
                    <div>
                      <div className="px-5 py-3 bg-error/10 border-b border-error/20">
                        <p className="text-sm text-error">
                          <span className="font-semibold">{bannedUsers.length}</span> usuário(s) bloqueado(s) para reservas de veículos.
                          Clique em "Desbloquear" para permitir que façam novas reservas.
                        </p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {bannedUsers.map((u) => (
                          <div key={u.id} className="p-4 flex items-center justify-between hover:bg-surface-container-low transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                                <Ban className="w-5 h-5 text-error" />
                              </div>
                              <div>
                                <div className="font-semibold text-on-surface">{u.nome}</div>
                                <div className="text-sm text-secondary">{u.email} — {u.setor}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnban(u.id, u.nome)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                            >
                              <UserCheck className="w-4 h-4" />
                              Desbloquear
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'veiculos' && (
                <div>
                  <div className="px-5 py-4 border-b border-outline-variant flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-on-surface">Gerenciamento de Veiculos</h3>
                      <p className="text-xs text-secondary mt-0.5">Adicione ou remova veiculos disponiveis para reserva</p>
                    </div>
                    <button
                      onClick={() => {
                        setNewVeiculo({ modelo: '', placa: '', cor_tag: '#3B82F6', observacao: '' });
                        setShowAddVeiculoModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar Veiculo
                    </button>
                  </div>

                  {veiculosLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : veiculosFrota.length === 0 ? (
                    <div className="text-center py-16">
                      <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-secondary">Nenhum veiculo cadastrado</p>
                      <button
                        onClick={() => setShowAddVeiculoModal(true)}
                        className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                      >
                        Cadastrar primeiro veiculo
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {veiculosFrota.map((v) => (
                        <div key={v.id} className="p-4 flex items-center justify-between hover:bg-surface-container-low transition-colors">
                          <div className="flex items-center gap-4">
                            <div
                              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: v.cor_tag + '20' }}
                            >
                              <Car className="w-5 h-5" style={{ color: v.cor_tag }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-on-surface">{v.modelo}</span>
                                <span className="text-sm text-secondary font-mono">{v.placa}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5">
                                {v.categoria && (
                                  <span className="text-xs text-secondary">{v.categoria}</span>
                                )}
                                {v.observacao && (
                                  <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-medium">
                                    {v.observacao}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full border-2" style={{ backgroundColor: v.cor_tag, borderColor: v.cor_tag }} />
                            <button
                              onClick={async () => {
                                if (!confirm(`Deseja remover o veiculo ${v.modelo} - ${v.placa} da frota?`)) return;
                                try {
                                  await removeVeiculo(v.id);
                                  toast.success(`${v.modelo} - ${v.placa} removido da frota.`);
                                } catch (err: any) {
                                  toast.error('Erro ao remover: ' + (err.message || 'Tente novamente'));
                                }
                              }}
                              className="p-2 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Remover veiculo"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {approvalState.reserva && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className={`px-6 py-5 rounded-t-2xl ${approvalState.action === 'approve' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                    {approvalState.action === 'approve'
                      ? <ShieldCheck className="w-5 h-5 text-green-600" />
                      : <ShieldX className="w-5 h-5 text-red-600" />
                    }
                  </div>
                  <h3 className="font-bold text-white text-lg">
                    {approvalState.action === 'approve' ? 'Aprovar Reserva' : 'Reprovar Reserva'}
                  </h3>
                </div>
                <button onClick={() => setApprovalState({ reserva: null, action: null, veiculoSelecionado: '', obs: '' })} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-surface-container-low rounded-xl p-4">
                <div className="font-semibold text-on-surface">{approvalState.reserva.nome}</div>
                <div className="text-sm text-secondary">{approvalState.reserva.motivo} — {approvalState.reserva.empresa}</div>
                <div className="text-xs text-secondary mt-1">
                  {approvalState.reserva.data_retirada} às {approvalState.reserva.horario_retirada?.slice(0, 5)} → {approvalState.reserva.data_entrega} às {approvalState.reserva.horario_entrega?.slice(0, 5)}
                </div>
                {approvalState.reserva.telefone && (
                  <div className="text-xs text-green-700 mt-1.5 flex items-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp será enviado para {approvalState.reserva.telefone}
                  </div>
                )}
              </div>

              {approvalState.action === 'approve' && (
                <div>
                  <label className="block text-sm font-semibold text-on-surface mb-2">
                    Veículo a liberar <span className="text-error">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {veiculosFrota.map((v) => {
                      const label = getVeiculoLabel(v);
                      const sel = approvalState.veiculoSelecionado === label;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setApprovalState((p) => ({ ...p, veiculoSelecionado: label }))}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${sel ? '' : 'border-outline-variant hover:border-primary/30'}`}
                          style={sel ? { borderColor: v.cor_tag, backgroundColor: v.cor_tag + '15' } : undefined}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: v.cor_tag + '25' }}>
                              <Car className="w-3.5 h-3.5" style={{ color: v.cor_tag }} />
                            </div>
                            <div>
                              <div className="font-semibold text-on-surface text-xs">{v.modelo}</div>
                              <div className="text-xs text-secondary">{v.placa}</div>
                            </div>
                            {sel && (
                              <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: v.cor_tag }}>
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1.5">
                  {approvalState.action === 'approve' ? 'Observações (opcional)' : <>Motivo da reprovação <span className="text-error">*</span></>}
                </label>
                <textarea
                  value={approvalState.obs}
                  onChange={(e) => setApprovalState((p) => ({ ...p, obs: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm resize-none"
                  rows={3}
                  placeholder={approvalState.action === 'approve' ? 'Instruções adicionais...' : 'Informe o motivo...'}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setApprovalState({ reserva: null, action: null, veiculoSelecionado: '', obs: '' })}
                  disabled={approvalLoading}
                  className="flex-1 px-4 py-2.5 border-2 border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low font-medium transition-colors disabled:opacity-50 text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApproval}
                  disabled={
                    approvalLoading ||
                    (approvalState.action === 'approve' && !approvalState.veiculoSelecionado) ||
                    (approvalState.action === 'reject' && !approvalState.obs.trim())
                  }
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 text-sm ${
                    approvalState.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-error hover:bg-red-700'
                  }`}
                >
                  {approvalLoading ? 'Processando...' : approvalState.action === 'approve' ? 'Confirmar Aprovação' : 'Confirmar Reprovação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {whatsappModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-bold text-white text-lg">Notificar via WhatsApp</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-on-surface text-sm mb-5">
                Enviar notificação para <span className="font-semibold">{whatsappModal.nome}</span> com o resultado da reserva?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setWhatsappModal(null)} className="flex-1 px-4 py-2.5 border-2 border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low font-medium transition-colors text-sm">
                  Pular
                </button>
                <a
                  href={whatsappModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setWhatsappModal(null)}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enviar WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddVeiculoModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                    <Car className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-white text-lg">Adicionar Veiculo</h3>
                </div>
                <button onClick={() => setShowAddVeiculoModal(false)} className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newVeiculo.modelo.trim() || !newVeiculo.placa.trim()) {
                  toast.error('Preencha modelo e placa.');
                  return;
                }
                setAddingVeiculo(true);
                try {
                  await addVeiculo({
                    modelo: newVeiculo.modelo.trim(),
                    placa: newVeiculo.placa.trim().toUpperCase(),
                    cor_tag: newVeiculo.cor_tag,
                    categoria: newVeiculo.modelo.trim(),
                    observacao: newVeiculo.observacao.trim() || undefined,
                  });
                  toast.success(`${newVeiculo.modelo} - ${newVeiculo.placa.toUpperCase()} adicionado!`);
                  setShowAddVeiculoModal(false);
                  setNewVeiculo({ modelo: '', placa: '', cor_tag: '#3B82F6', observacao: '' });
                } catch (err: any) {
                  const msg = err?.message || 'Tente novamente';
                  if (msg.includes('duplicate') || msg.includes('unique')) {
                    toast.error('Ja existe um veiculo com essa placa.');
                  } else {
                    toast.error('Erro ao adicionar: ' + msg);
                  }
                } finally {
                  setAddingVeiculo(false);
                }
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-on-surface mb-1.5">Modelo *</label>
                <input
                  type="text"
                  value={newVeiculo.modelo}
                  onChange={(e) => setNewVeiculo({ ...newVeiculo, modelo: e.target.value })}
                  className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                  placeholder="Ex: Fiat Toro, Renault Kwid..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1.5">Placa *</label>
                <input
                  type="text"
                  value={newVeiculo.placa}
                  onChange={(e) => setNewVeiculo({ ...newVeiculo, placa: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-mono uppercase"
                  placeholder="Ex: ABC1D23"
                  maxLength={8}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1.5">Cor de identificacao</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={newVeiculo.cor_tag}
                    onChange={(e) => setNewVeiculo({ ...newVeiculo, cor_tag: e.target.value })}
                    className="w-10 h-10 rounded-lg border border-outline-variant cursor-pointer"
                  />
                  <div className="flex flex-wrap gap-2">
                    {['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#EF4444', '#8B5CF6', '#6366F1'].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewVeiculo({ ...newVeiculo, cor_tag: c })}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${newVeiculo.cor_tag === c ? 'scale-110 border-on-surface' : 'border-transparent hover:scale-105'}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1.5">Observacao (opcional)</label>
                <input
                  type="text"
                  value={newVeiculo.observacao}
                  onChange={(e) => setNewVeiculo({ ...newVeiculo, observacao: e.target.value })}
                  className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                  placeholder="Ex: Emergencial, Uso interno..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddVeiculoModal(false)}
                  className="flex-1 px-4 py-2.5 border-2 border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low font-medium transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={addingVeiculo}
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-semibold transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {addingVeiculo ? 'Adicionando...' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};